import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import Breadcrumb from '../../../core/common/Breadcrumb/breadcrumb'
import ImageWithBasePath from '../../../core/common/imageWithBasePath'
import StudentSidebar from '../common/studentSidebar'
import ProfileCard from '../common/profileCard'
import { all_routes } from '../../router/all_routes'
import {
  calculateDiscount,
  rupiahFormatter,
  downloadCertificate,
  getStoredCertificateName,
  prepareCertificateDownload,
  resolveCertificateSignatureDate,
  setStoredCertificateName,
  CertificateMeta,
} from '../../../utils/helpers'
import { getCBTDetail } from '../../../api/courses'
import {
  getPurchasedCourseDetail,
  PurchasedCourseDetailResponse,
} from '../../../api/user'

function formatDateWithTimeZone(dateString: string) {
  if (!dateString) return ''
  const date = new Date(dateString)
  return new Intl.DateTimeFormat('id-ID', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Asia/Jakarta',
    timeZoneName: 'short',
  })
    .format(date)
    .replace('GMT+7', 'WIB')
}

const LiveEventDetails = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [showCertificateModal, setShowCertificateModal] = useState(false)
  const [certificateName, setCertificateName] = useState('')
  const [storedCertificateName, setStoredCertificateNameState] = useState('')
  const [countdown, setCountdown] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
  })
  const [attemptInfo, setAttemptInfo] = useState({
    maxAttempt: 0,
    usedAttempt: 0,
  })
  const [showGroupModal, setShowGroupModal] = useState<boolean>(false)
  const [showFeedbackModal, setShowFeedbackModal] = useState<boolean>(false)
  const [hasClickedFeedback, setHasClickedFeedback] = useState<boolean>(() => {
    if (!id) return false
    return localStorage.getItem(`feedback_clicked_${id}`) === 'true'
  })

  // Fetch course data using the new API
  const { data: courseResponse, isLoading, isError, error } =
    useQuery<PurchasedCourseDetailResponse>({
    queryKey: ['purchasedCourseDetail', id],
    queryFn: () => getPurchasedCourseDetail(id!),
    enabled: !!id,
  })

  const course = courseResponse?.data
  const savedCertificateName =
    course?.certificate_downloads?.name || storedCertificateName

  // Countdown effect for live teaching
  useEffect(() => {
    if (!course?.start_date) return

    const timer = setInterval(() => {
      const now = new Date().getTime()
      const startDate = new Date(course.start_date).getTime()
      const timeLeft = startDate - now

      if (timeLeft > 0) {
        const days = Math.floor(timeLeft / (1000 * 60 * 60 * 24))
        const hours = Math.floor(
          (timeLeft % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60),
        )
        const minutes = Math.floor((timeLeft % (1000 * 60 * 60)) / (1000 * 60))
        const seconds = Math.floor((timeLeft % (1000 * 60)) / 1000)

        setCountdown({ days, hours, minutes, seconds })
      } else {
        setCountdown({ days: 0, hours: 0, minutes: 0, seconds: 0 })
      }
    }, 1000)

    return () => clearInterval(timer)
  }, [course?.start_date])

  // Determine event status
  const getEventStatus = () => {
    if (!course?.start_date || !course?.end_date) return 'upcoming'

    const now = new Date().getTime()
    const startDate = new Date(course.start_date).getTime()
    const endDate = new Date(course.end_date).getTime()

    if (now > endDate) {
      return 'ended'
    }
    if (now >= startDate && now <= endDate) {
      return 'live'
    }
    return 'upcoming'
  }

  const eventStatus = getEventStatus()
  const hasFeedbackLink = Boolean(
    course?.link_feedback &&
      String(course.link_feedback).trim() !== '' &&
      String(course.link_feedback).trim() !== 'null' &&
      String(course.link_feedback).trim() !== 'undefined',
  )
  const isFeedbackRequiredAndPending = hasFeedbackLink && !hasClickedFeedback
  const hasLiveTeachingCbt = Number(course?.questions_count || 0) > 0
  const isCbtRequiredAndPending = hasLiveTeachingCbt && attemptInfo.usedAttempt <= 0
  const isCertificateLocked =
    eventStatus !== 'ended' || isFeedbackRequiredAndPending || isCbtRequiredAndPending

  const handleFeedbackClick = () => {
    if (!hasFeedbackLink || eventStatus !== 'ended') return

    if (id) {
      localStorage.setItem(`feedback_clicked_${id}`, 'true')
    }
    setHasClickedFeedback(true)
    window.open(course.link_feedback, '_blank')
  }

  const handleDownloadSertifikatClick = () => {
    if (eventStatus !== 'ended') {
      alert('Sertifikat belum bisa diunduh. Waktu kelas belum selesai, mohon tunggu hingga kelas berakhir.')
      return
    }
    if (isFeedbackRequiredAndPending) {
      setShowFeedbackModal(true)
      return
    }
    handleDownloadSertifikat()
  }

  useEffect(() => {
    if (!savedCertificateName || !course?.id) return
    if (
      course.certificate_downloads?.certificate_number ||
      course.certificate_downloads?.qr_url
    ) {
      return
    }

    prepareCertificateDownload(Number(course.id), savedCertificateName)
  }, [
    course?.id,
    course?.certificate_downloads?.name,
    course?.certificate_downloads?.certificate_number,
    course?.certificate_downloads?.qr_url,
    savedCertificateName,
  ])

  useEffect(() => {
    setStoredCertificateNameState(getStoredCertificateName(course?.id))
  }, [course?.id])

  const handleJoinGroup = () => {
    if (course?.link_group && course?.link_group_loker) {
      setShowGroupModal(true)
    } else if (course?.link_group) {
      window.open(course.link_group, '_blank')
    } else if (course?.link_group_loker) {
      window.open(course.link_group_loker, '_blank')
    }
  }

  const handleJoinKelas = () => {
    // For live events, you might want to use a different link or generate one
    // For now, using the group link as placeholder
    if (course?.link_group) {
      window.open(course.link_group, '_blank')
    }
  }

  const handleAksesRekaman = () => {
    // Access the recording link from the API response
    if (course?.link_record) {
      window.open(course.link_record, '_blank')
    } else {
      alert('Link rekaman belum tersedia')
    }
  }

  const handleDownloadSertifikat = async () => {
    if (isCertificateLocked) {
      alert('Sertifikat belum tersedia. Unduh setelah tanggal kelas live teaching selesai.')
      return
    }

    const materialTopicOnly =
      Number(course?.questions_count || course?.questions?.length || 0) <= 0
    const certificateMeta: CertificateMeta = {
      eventDates: course?.certificate_event_dates || undefined,
      jplTotal: course?.certificate_jpl_total || undefined,
      jplNote: course?.certificate_jpl_note || undefined,
      speakers: course?.certificate_speakers || undefined,
      materials: course?.certificate_materials || undefined,
      materialTopicOnly,
      location: course?.certificate_location || undefined,
      signatureDate: resolveCertificateSignatureDate(
        course?.certificate_event_dates || undefined,
        course?.certificate_signature_date || course?.end_date || undefined,
      ),
      certificateNumber:
        course?.certificate_downloads?.certificate_number || undefined,
      qrUrl: course?.certificate_downloads?.qr_url || undefined,
    }

    // Check if certificate_downloads exists and has a name
    if (savedCertificateName) {
      // Direct download with existing name
      const courseTopics =
        course?.studied?.map((topic) => ({ name: topic })) || []

      try {
        await downloadCertificate(
          course?.title || 'Live Teaching Certificate',
          undefined, // customFileName
          undefined, // pdfUrl
          savedCertificateName, // userName - use existing name
          course?.title, // courseTitle
          true, // useAutoGeneration
          course?.id, // courseId
          courseTopics, // courseTopics
          false,
          '',
          '',
          certificateMeta,
        )
      } catch (error) {
        console.error('Error downloading certificate:', error)
        alert('Gagal mengunduh sertifikat. Silakan coba lagi.')
      }
    } else {
      // Show modal for first time download (certificate_downloads is null)
      handleOpenModal()
    }
  }

  const handleOpenModal = () => {
    // Reset certificate name when opening modal
    setCertificateName('')
    setShowCertificateModal(true)
  }

  const handleConfirmDownload = async () => {
    if (!certificateName.trim()) {
      alert('Nama lengkap harus diisi')
      return
    }

    const courseTopics =
      course?.studied?.map((topic) => ({ name: topic })) || []
    const materialTopicOnly =
      Number(course?.questions_count || course?.questions?.length || 0) <= 0
    const certificateMeta: CertificateMeta = {
      eventDates: course?.certificate_event_dates || undefined,
      jplTotal: course?.certificate_jpl_total || undefined,
      jplNote: course?.certificate_jpl_note || undefined,
      speakers: course?.certificate_speakers || undefined,
      materials: course?.certificate_materials || undefined,
      materialTopicOnly,
      location: course?.certificate_location || undefined,
      signatureDate: resolveCertificateSignatureDate(
        course?.certificate_event_dates || undefined,
        course?.certificate_signature_date || course?.end_date || undefined,
      ),
      certificateNumber:
        course?.certificate_downloads?.certificate_number || undefined,
      qrUrl: course?.certificate_downloads?.qr_url || undefined,
    }

    try {
      setStoredCertificateName(course?.id, certificateName)
      setStoredCertificateNameState(certificateName)

      // Download certificate with user's name
      await downloadCertificate(
        course?.title || 'Live Teaching Certificate',
        undefined, // customFileName
        undefined, // pdfUrl
        certificateName, // userName - use input name
        course?.title, // courseTitle
        true, // useAutoGeneration
        course?.id, // courseId
        courseTopics, // courseTopics
        false,
        '',
        '',
        certificateMeta,
      )

      setShowCertificateModal(false)
    } catch (error) {
      console.error('Error downloading certificate:', error)
      alert('Gagal mengunduh sertifikat. Silakan coba lagi.')
    }
  }

  const handleDownloadMateri = () => {
    if (course?.link_ebook) {
      window.open(course.link_ebook, '_blank')
    }
  }

  const handleStartQuiz = () => {
    if (course?.id) {
      navigate(all_routes.studentCBTInstruction.replace(':id', String(course.id)))
    }
  }

  useEffect(() => {
    const fetchAttemptInfo = async () => {
      if (!course?.id) return
      const questionCount = Number(course?.questions_count || course?.questions?.length || 0)
      if (questionCount <= 0) return

      try {
        const response = await getCBTDetail(String(course.id))
        const maxAttempt = Number(response.data.max_attempt || 0)
        const usedAttempt = Number(response.data.user_attempt_count || 0)
        setAttemptInfo({ maxAttempt, usedAttempt })
      } catch {
        setAttemptInfo({ maxAttempt: 0, usedAttempt: 0 })
      }
    }

    fetchAttemptInfo()
  }, [course?.id, course?.questions_count, course?.questions])

  const remainingAttempt = Math.max(
    0,
    attemptInfo.maxAttempt - attemptInfo.usedAttempt,
  )

  if (isLoading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-2">Loading live event details...</p>
      </div>
    )
  }

  if (isError) {
    return (
      <div className="text-center py-5 text-danger">
        <p>Error loading live event: {error?.message || 'Unknown error'}</p>
        <p>Please try again later.</p>
      </div>
    )
  }

  if (!course) {
    return <div>Live event not found.</div>
  }

  return (
    <>
      <Breadcrumb title={course.title} />

      <div className="content">
        <div className="container">
          {/* profile box */}
          <ProfileCard />
          {/* profile box */}
          <div className="row">
            {/* sidebar */}
            <StudentSidebar />
            {/* sidebar */}
            <div className="col-lg-9">
              <div className="card bg-light mb-4">
                <div className="card-body d-lg-flex align-items-center">
                  <div className="position-relative">
                    <ImageWithBasePath
                      className="img-fluid rounded-2"
                      src={course.image || '/assets/img/course/course-48.jpg'}
                      fallbackSrc="/assets/img/course/course-48.jpg"
                      alt={course.title}
                    />
                  </div>
                  <div className="w-100 ps-lg-4">
                    <h3 className="mb-2">{course.title}</h3>
                    <p className="fs-14 mb-3">{course.short_description}</p>
                    <div className="d-flex align-items-center gap-2 gap-sm-3 gap-xl-4 flex-wrap my-3 my-sm-0">
                      <p className="fw-medium d-flex align-items-center mb-0">
                        {course.start_date && course.end_date
                          ? `${formatDateWithTimeZone(
                              course.start_date,
                            )} - ${formatDateWithTimeZone(course.end_date)}`
                          : 'Date not specified'}
                      </p>
                      <span className="badge badge-sm rounded-pill bg-warning fs-12">
                        {course.category?.name}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Event Status and Action Buttons */}
              <div className="card mb-4">
                <div className="card-body">
                  <div className="row">
                    <div className="col-md-8">
                      <h5 className="mb-3">Event Status</h5>
                      {eventStatus === 'upcoming' && (
                        <div className="alert alert-info border-0 shadow-sm" style={{ borderRadius: '12px' }}>
                          <strong>Upcoming Event</strong>
                          <p className="mb-0 mt-2">
                            Event will start in:{' '}
                            {countdown.days > 0 && <>{countdown.days} hari </>}
                            {countdown.hours.toString().padStart(2, '0')} :{' '}
                            {countdown.minutes.toString().padStart(2, '0')} :{' '}
                            {countdown.seconds.toString().padStart(2, '0')}
                          </p>
                        </div>
                      )}
                      {eventStatus === 'live' && (
                        <div className="alert alert-success border-0 shadow-sm" style={{ borderRadius: '12px' }}>
                          <strong>Live Now!</strong>
                          <p className="mb-0 mt-2">
                            The event is currently live. Join the class now!
                          </p>
                        </div>
                      )}
                      {eventStatus === 'ended' && (
                        <div className="alert alert-secondary border-0 shadow-sm" style={{ borderRadius: '12px' }}>
                          <strong>Event Ended</strong>
                          <p className="mb-0 mt-2">
                            This event has ended. You can access the recording
                            and download materials.
                          </p>
                        </div>
                      )}

                      {/* Keterangan Cara Mendapatkan Sertifikat */}
                      <div
                        className="mt-3 p-3 p-sm-3.5"
                        style={{
                          backgroundColor: '#f8fafc',
                          borderRadius: '14px',
                          border: '1px solid #e2e8f0',
                          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.02)',
                        }}
                      >
                        <div className="d-flex align-items-center gap-2 mb-3">
                          <div
                            className="d-flex align-items-center justify-content-center text-white shadow-sm flex-shrink-0"
                            style={{
                              width: '28px',
                              height: '28px',
                              borderRadius: '8px',
                              background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                            }}
                          >
                            <i className="isax isax-award fs-16" />
                          </div>
                          <h6 className="fw-bold mb-0 text-dark" style={{ fontSize: '14px', color: '#0f172a' }}>
                            Cara Mendapatkan Sertifikat
                          </h6>
                        </div>

                        <div className="d-flex flex-column" style={{ gap: '12px' }}>
                          <div className="d-flex align-items-start">
                            <span
                              className="badge rounded-circle text-white d-flex align-items-center justify-content-center flex-shrink-0 me-3"
                              style={{
                                width: '22px',
                                height: '22px',
                                backgroundColor: '#2563eb',
                                fontSize: '11px',
                                fontWeight: 700,
                                marginTop: '1px',
                              }}
                            >
                              1
                            </span>
                            <span style={{ fontSize: '13px', lineHeight: '1.45', color: '#334155' }}>
                              Pastikan <strong style={{ color: '#0f172a' }}>tanggal dan waktu kelas telah selesai</strong>.
                            </span>
                          </div>

                          <div className="d-flex align-items-start">
                            <span
                              className="badge rounded-circle text-white d-flex align-items-center justify-content-center flex-shrink-0 me-3"
                              style={{
                                width: '22px',
                                height: '22px',
                                backgroundColor: '#4361ee',
                                fontSize: '11px',
                                fontWeight: 700,
                                marginTop: '1px',
                              }}
                            >
                              2
                            </span>
                            <span style={{ fontSize: '13px', lineHeight: '1.45', color: '#334155' }}>
                              Klik <strong style={{ color: '#0f172a' }}>Feedback Peserta</strong>, dan isi feedbacknya.
                            </span>
                          </div>

                          <div className="d-flex align-items-start">
                            <span
                              className="badge rounded-circle text-white d-flex align-items-center justify-content-center flex-shrink-0 me-3"
                              style={{
                                width: '22px',
                                height: '22px',
                                backgroundColor: '#d97706',
                                fontSize: '11px',
                                fontWeight: 700,
                                marginTop: '1px',
                              }}
                            >
                              3
                            </span>
                            <span style={{ fontSize: '13px', lineHeight: '1.45', color: '#334155' }}>
                              Klik <strong style={{ color: '#0f172a' }}>Download Sertifikat</strong>.
                            </span>
                          </div>

                          <div className="d-flex align-items-start">
                            <span
                              className="badge rounded-circle text-white d-flex align-items-center justify-content-center flex-shrink-0 me-3"
                              style={{
                                width: '22px',
                                height: '22px',
                                backgroundColor: '#059669',
                                fontSize: '11px',
                                fontWeight: 700,
                                marginTop: '1px',
                              }}
                            >
                              4
                            </span>
                            <span style={{ fontSize: '13px', lineHeight: '1.45', color: '#334155' }}>
                              Input <strong style={{ color: '#0f172a' }}>Nama dan Gelar</strong>, pastikan data sesuai (generate sertifikat <strong style={{ color: '#dc2626' }}>hanya bisa 1x</strong>).
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <h5 className="mb-3">Actions</h5>
                      <div className="d-grid gap-2" style={{ gap: '8px' }}>
                        {attemptInfo.maxAttempt > 0 && (
                          <div className="alert alert-info py-2 mb-0">
                            <i className="fas fa-hourglass-half me-2" />
                            Sisa percobaan kuis: {remainingAttempt} dari{' '}
                            {attemptInfo.maxAttempt}
                          </div>
                        )}
                        {course.link_group && (
                          <button
                            type="button"
                            className="btn text-white fw-semibold shadow-sm d-flex align-items-center justify-content-center border-0 w-100"
                            style={{
                              backgroundColor: '#00bcd4',
                              borderRadius: '50px',
                              padding: '8px 16px',
                              minHeight: '38px',
                              fontSize: '13.5px',
                              fontWeight: 600,
                              letterSpacing: '0.1px',
                            }}
                            onClick={() => window.open(course.link_group, '_blank')}
                          >
                            <i className="isax isax-people me-2 fs-16" />
                            <span>Join Group Diskusi</span>
                          </button>
                        )}

                        {Boolean(course.link_group_loker && String(course.link_group_loker).trim() !== '' && String(course.link_group_loker).trim() !== 'null' && String(course.link_group_loker).trim() !== 'undefined') && (
                          <button
                            type="button"
                            className="btn text-white fw-semibold shadow-sm d-flex align-items-center justify-content-center border-0 w-100"
                            style={{
                              backgroundColor: '#2ec4b6',
                              borderRadius: '50px',
                              padding: '8px 16px',
                              minHeight: '38px',
                              fontSize: '13.5px',
                              fontWeight: 600,
                              letterSpacing: '0.1px',
                            }}
                            onClick={() => window.open(course.link_group_loker, '_blank')}
                          >
                            <i className="isax isax-briefcase me-2 fs-16" />
                            <span>Join Group Lowongan Kerja</span>
                          </button>
                        )}

                        {course?.is_answered && (
                          <button
                            className="btn text-white fw-semibold shadow-sm d-flex align-items-center justify-content-center border-0 w-100"
                            style={{
                              backgroundColor: '#059669',
                              borderRadius: '50px',
                              padding: '8px 16px',
                              minHeight: '38px',
                              fontSize: '13.5px',
                              fontWeight: 600,
                              letterSpacing: '0.1px',
                            }}
                            onClick={() =>
                              navigate(
                                all_routes.studentCBTList.replace(
                                  ':id',
                                  String(course.id),
                                ),
                              )
                            }
                          >
                            <i className="isax isax-eye me-2 fs-16" />
                            <span>Detail Skor</span>
                          </button>
                        )}

                        {Number(course?.questions_count || course?.questions?.length || 0) > 0 &&
                          !course?.is_finished &&
                          remainingAttempt > 0 && (
                          <button
                            className="btn text-white fw-semibold shadow-sm d-flex align-items-center justify-content-center border-0 w-100"
                            style={{
                              backgroundColor: '#6366f1',
                              borderRadius: '50px',
                              padding: '8px 16px',
                              minHeight: '38px',
                              fontSize: '13.5px',
                              fontWeight: 600,
                              letterSpacing: '0.1px',
                            }}
                            onClick={handleStartQuiz}
                          >
                            <i className="isax isax-play-circle me-2 fs-16" />
                            <span>Mulai Kuis</span>
                          </button>
                        )}

                        {eventStatus === 'live' && (
                          <button
                            className="btn text-white fw-semibold shadow-sm d-flex align-items-center justify-content-center border-0 w-100"
                            style={{
                              backgroundColor: '#10b981',
                              borderRadius: '50px',
                              padding: '8px 16px',
                              minHeight: '38px',
                              fontSize: '13.5px',
                              fontWeight: 600,
                              letterSpacing: '0.1px',
                            }}
                            onClick={handleJoinKelas}
                          >
                            <i className="isax isax-video me-2 fs-16" />
                            <span>Join Kelas</span>
                          </button>
                        )}

                        {course?.link_record && (
                          <button
                            type="button"
                            className="btn text-white fw-semibold shadow-sm d-flex align-items-center justify-content-center border-0 w-100"
                            style={{
                              backgroundColor: '#7209b7',
                              borderRadius: '50px',
                              padding: '8px 16px',
                              minHeight: '38px',
                              fontSize: '13.5px',
                              fontWeight: 600,
                              letterSpacing: '0.1px',
                            }}
                            onClick={handleAksesRekaman}
                          >
                            <i className="isax isax-video-play me-2 fs-16" />
                            <span>Akses Rekaman</span>
                          </button>
                        )}

                      <button
                        type="button"
                        className="btn text-white fw-semibold shadow-sm d-flex align-items-center justify-content-center flex-wrap border-0 w-100"
                        disabled={isCertificateLocked}
                        style={{
                          backgroundColor: isCertificateLocked ? '#94a3b8' : '#f59e0b',
                          borderRadius: '50px',
                          padding: '8px 16px',
                            minHeight: '38px',
                            fontSize: '13.5px',
                            fontWeight: 600,
                            letterSpacing: '0.1px',
                            cursor: isCertificateLocked ? 'not-allowed' : 'pointer',
                            opacity: isCertificateLocked ? 0.85 : 1,
                          }}
                          onClick={handleDownloadSertifikatClick}
                        >
                          <i className={`isax ${isCertificateLocked ? 'isax-lock' : 'isax-award'} me-2 fs-16`} />
                          <span>Download Sertifikat</span>
                          {isCertificateLocked && eventStatus !== 'ended' && (
                            <span className="badge bg-secondary ms-2 shadow-sm" style={{ fontSize: '10.5px', padding: '3px 7px', borderRadius: '20px' }}>
                              Tunggu Kelas Selesai
                            </span>
                          )}
                          {isFeedbackRequiredAndPending && eventStatus === 'ended' && (
                            <span className="badge bg-danger ms-2 shadow-sm" style={{ fontSize: '10.5px', padding: '3px 7px', borderRadius: '20px' }}>
                              Isi Feedback Dulu
                            </span>
                          )}
                        </button>

                        {course.link_ebook && (
                          <button
                            type="button"
                            className="btn text-white fw-semibold shadow-sm d-flex align-items-center justify-content-center border-0 w-100"
                            style={{
                              backgroundColor: '#6c5ce7',
                              borderRadius: '50px',
                              padding: '8px 16px',
                              minHeight: '38px',
                              fontSize: '13.5px',
                              fontWeight: 600,
                              letterSpacing: '0.1px',
                            }}
                            onClick={handleDownloadMateri}
                          >
                            <i className="isax isax-document-download me-2 fs-16" />
                            <span>Download Materi</span>
                          </button>
                        )}

                      {hasFeedbackLink && (
                        <button
                          type="button"
                          className="btn text-white fw-semibold shadow-sm d-flex align-items-center justify-content-center border-0 w-100"
                          style={{
                            backgroundColor:
                              eventStatus !== 'ended' ? '#94a3b8' : '#4361ee',
                            borderRadius: '50px',
                            padding: '8px 16px',
                            minHeight: '38px',
                            fontSize: '13.5px',
                            fontWeight: 600,
                            letterSpacing: '0.1px',
                            cursor: eventStatus !== 'ended' ? 'not-allowed' : 'pointer',
                            opacity: eventStatus !== 'ended' ? 0.85 : 1,
                          }}
                          disabled={eventStatus !== 'ended'}
                          onClick={handleFeedbackClick}
                        >
                            <i className="isax isax-message-edit me-2 fs-16" />
                            <span>Feedback Peserta</span>
                            {hasClickedFeedback && (
                              <i className="isax isax-tick-circle text-white ms-2" />
                            )}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Course Details */}
              <div className="card mb-4">
                <div className="card-body">
                  <h5 className="mb-3">Course Details</h5>
                  <div className="row">
                    <div className="col-md-6">
                      <p>
                        <strong>Category:</strong> {course.category?.name}
                      </p>
                      <p>
                        <strong>Level:</strong> {course.course_level}
                      </p>
                      <p>
                        <strong>Students:</strong> {course.student_count}
                      </p>
                    </div>
                    <div className="col-md-6">
                      <p>
                        <strong>Max Students:</strong> {course.max_student}
                      </p>
                      <p>
                        <strong>Price:</strong>
                        {course.discount ? (
                          <span>
                            {calculateDiscount(
                              parseFloat(course.price),
                              course.discount,
                              course.discount_type as 'PERCENTAGE' | 'NOMINAL',
                            )}
                            <span className="text-decoration-line-through ms-2">
                              {rupiahFormatter(parseFloat(course.price))}
                            </span>
                          </span>
                        ) : (
                          rupiahFormatter(parseFloat(course.price))
                        )}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Course Description */}
              <div className="card">
                <div className="card-body">
                  <h5 className="mb-3">Description</h5>
                  <div
                    dangerouslySetInnerHTML={{ __html: course.description }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Certificate Download Modal */}
      {showCertificateModal && (
        <div
          className="modal fade show"
          style={{ display: 'block' }}
          tabIndex={-1}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Download Sertifikat</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowCertificateModal(false)
                    // Reset certificate name when modal is closed
                    setCertificateName('')
                  }}
                ></button>
              </div>
              <div className="modal-body">
                <p>
                  Masukkan nama lengkap Anda yang akan ditampilkan pada
                  sertifikat. Nama ini akan digunakan untuk generate sertifikat
                  secara otomatis.
                </p>
                <div className="mb-3">
                  <label className="form-label">Nama Lengkap</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Masukkan nama lengkap Anda (contoh: John Doe)"
                    value={certificateName}
                    onChange={(e) => setCertificateName(e.target.value)}
                    autoFocus
                  />
                </div>
                <div className="alert alert-warning">
                  <i className="fas fa-exclamation-triangle me-2"></i>
                  Pastikan nama sudah benar, karena e-sertifikat hanya dapat di
                  generate satu kali.
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowCertificateModal(false)
                    // Reset certificate name when modal is closed
                    setCertificateName('')
                  }}
                >
                  Batal
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleConfirmDownload}
                  disabled={!certificateName.trim()}
                >
                  Download Sertifikat
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Backdrop */}
      {showCertificateModal && <div className="modal-backdrop fade show"></div>}

      {/* MODAL JOIN GROUP */}
      {showGroupModal && (
        <div
          className="modal fade show"
          style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1060 }}
          tabIndex={-1}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content rounded-4 border-0 shadow">
              <div className="modal-header border-bottom-0 pb-0">
                <h5 className="modal-title fw-bold text-dark">
                  <i className="isax isax-people text-info me-2" />
                  Pilih Group
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowGroupModal(false)}
                />
              </div>
              <div className="modal-body py-4">
                <p className="text-muted small mb-4">
                  Silakan pilih group WhatsApp / Telegram yang ingin Anda ikuti:
                </p>
                <div className="d-grid gap-3">
                  {course?.link_group && (
                    <a
                      href={course.link_group}
                      target="_blank"
                      rel="noreferrer"
                      className="btn text-white fw-bold py-3 rounded-pill d-flex align-items-center justify-content-center shadow-sm"
                      style={{ backgroundColor: '#00bcd4', fontSize: '15px' }}
                    >
                      <i className="isax isax-people me-2 fs-18" />
                      Join Group Peserta
                    </a>
                  )}
                  {course?.link_group_loker && (
                    <a
                      href={course.link_group_loker}
                      target="_blank"
                      rel="noreferrer"
                      className="btn text-white fw-bold py-3 rounded-pill d-flex align-items-center justify-content-center shadow-sm"
                      style={{ backgroundColor: '#0284c7', fontSize: '15px' }}
                    >
                      <i className="isax isax-briefcase me-2 fs-18" />
                      Join Group Lowongan Kerja
                    </a>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* MODAL FEEDBACK REQUIREMENT */}
      {showFeedbackModal && (
        <div
          className="modal fade show"
          style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1065 }}
          tabIndex={-1}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content rounded-4 border-0 shadow">
              <div className="modal-header border-bottom-0 pb-0">
                <h5 className="modal-title fw-bold text-dark">
                  <i className="isax isax-message-edit text-warning me-2" />
                  Feedback Peserta Diperlukan
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowFeedbackModal(false)}
                />
              </div>
              <div className="modal-body py-4 text-center">
                <div
                  className="mb-3 d-inline-flex align-items-center justify-content-center rounded-circle bg-light-warning"
                  style={{ width: 64, height: 64 }}
                >
                  <i className="isax isax-warning-2 text-warning fs-32" />
                </div>
                <h6 className="fw-semibold mb-2">Isi Feedback Peserta Terlebih Dahulu</h6>
                <p className="text-muted small mb-4 px-3">
                  Button download sertifikat hanya bisa dibuka ketika Anda sudah mengisi feedback peserta. Silakan klik tombol di bawah untuk mengisi form masukan.
                </p>
                <div className="d-grid gap-2">
                  <button
                    type="button"
                    className="btn text-white fw-bold py-3 rounded-pill d-flex align-items-center justify-content-center shadow-sm"
                    style={{ backgroundColor: '#4361ee', fontSize: '15px' }}
                    onClick={() => {
                      setShowFeedbackModal(false)
                      handleFeedbackClick()
                    }}
                  >
                    <i className="isax isax-message-edit me-2 fs-18" />
                    Isi Feedback Peserta Sekarang
                  </button>
                  <button
                    type="button"
                    className="btn btn-light rounded-pill py-2 text-muted fw-semibold"
                    onClick={() => setShowFeedbackModal(false)}
                  >
                    Nanti Saja
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default LiveEventDetails
